import { create } from 'zustand';
import update from 'immutability-helper';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { SuggestedAction } from './ActionBubbles/types';

// deep clone if using nested objects
export const defaultState = {
  events: [],
  id: null,
  dashboardConfigMap: {},
  entrypointParams: null,
};

// Reusable event type
export interface NexaEvent {
  id?: string;
  author: string;
  type: string;
  value: any;
  timestamp?: string;
  thought?: boolean;
}

interface NexaState {
  events: NexaEvent[];
  id: string | null;
  dashboardConfigMap: { [key: string]: any };
  entrypointParams: {
    analysis_id?: number;
    diagnostic_type?: string;
    diagnostic_params?: any;
  } | null;
}

export const useNexaStore = create<NexaState>((set) => ({
  ...defaultState,
}));

export const initNexaStore = (initState) => {
  useNexaStore.setState((state) => ({ ...state, ...initState }));
};

// without subscription
export const getEvents = () => {
  const { events } = useNexaStore.getState();
  return events;
};

export const pushEvent = (event: NexaEvent) => {
  useNexaStore.setState((state) => {
    return update(state, {
      events: {
        $push: [event],
      },
    });
  });
};

export const updateOrPushEvent = (event: NexaEvent) => {
  useNexaStore.setState((state) => {
    const existingIndex = state.events.findIndex((e) => e.id === event.id);
    if (existingIndex >= 0) {
      // Update existing event (for partial streaming)
      return update(state, {
        events: {
          [existingIndex]: { $set: event },
        },
      });
    } else {
      // Push new event
      return update(state, {
        events: { $push: [event] },
      });
    }
  });
};

export const useEvents = () => {
  const { events } = useNexaStore((state) => state);
  return events;
};

export const resetNexaStore = () => {
  useNexaStore.setState(() => defaultState);
};

// merge input state with the existing dashboardConfigMap, overwriting any existing values
export const setDashboardConfigMap = (inputConfigMap: { [key: string]: any }) => {
  useNexaStore.setState((state) => {
    return update(state, {
      dashboardConfigMap: { $merge: inputConfigMap },
    });
  });
};

export const getSessionId = () => {
  const { id } = useNexaStore.getState();
  return id;
};

export const getEntrypointParams = () => {
  const { entrypointParams } = useNexaStore.getState();
  return entrypointParams;
};

export const setEntrypointParams = (
  params: {
    analysis_id?: number;
    diagnostic_type?: string;
    diagnostic_params?: any;
  } | null,
) => {
  useNexaStore.setState((state) => ({
    ...state,
    entrypointParams: params,
  }));
};

export const isSharedView = () => {
  const id = getSessionId();
  return id?.startsWith('sh_') ?? false;
};

// atom state
export const panelWidthAtom = atom(100);
export const chatStreamLoadingAtom = atom(false);
export const isDashboardGeneratedAtom = atom(false);

// ============================================================================
// Agent Status atoms
// ============================================================================

export interface AgentStatusState {
  streamId: string | null;
  phase: string | null;
  message: string | null;
  seq: number;
  displayedAtMs: number;
  pendingTransition: { phase: string; message: string; seq: number } | null;
  terminalPendingClear: boolean;
  responseRendered: boolean;
}

export const DEFAULT_AGENT_STATUS: AgentStatusState = {
  streamId: null,
  phase: null,
  message: null,
  seq: 0,
  displayedAtMs: 0,
  pendingTransition: null,
  terminalPendingClear: false,
  responseRendered: false,
};

export const agentStatusAtom = atom<AgentStatusState>(DEFAULT_AGENT_STATUS);

// Read-only derived atom: only re-renders consumers when message changes
export const agentStatusMessageDerivedAtom = atom((get) => get(agentStatusAtom).message);

// ============================================================================
// Report atoms
// ============================================================================

export const isReportPanelOpenAtom = atom(false);
export const reportHighlightAtom = atom(0); // Counter instead of boolean - increment to trigger animation
export const activeReportVersionAtom = atom<number | null>(null); // Active/selected report version (single source of truth)
export const isReportGeneratingAtom = atom(false); // Loading state for tool-triggered report generation

export interface ReportStreamState {
  streamId: string | null;
  phase: string | null;
  message: string | null;
  html: string;
  seq: number;
  version: number | null;
}

export const DEFAULT_REPORT_STREAM: ReportStreamState = {
  streamId: null,
  phase: null,
  message: null,
  html: '',
  seq: 0,
  version: null,
};

export const reportStreamAtom = atom<ReportStreamState>(DEFAULT_REPORT_STREAM);

// Read-only derived atoms: each consumer only re-renders when its field changes
export const reportStreamPhaseDerivedAtom = atom((get) => get(reportStreamAtom).phase);
export const reportStreamMessageDerivedAtom = atom((get) => get(reportStreamAtom).message);
export const reportStreamHtmlDerivedAtom = atom((get) => get(reportStreamAtom).html);
export const reportStreamVersionDerivedAtom = atom((get) => get(reportStreamAtom).version);

// Settings atoms
export const settingsModalVisibleAtom = atom(false);
export const activeSettingsTabAtom = atom<string | null>(null);
export const showReasoningAtom = atomWithStorage('nexa-show-reasoning', true);
export const showActionButtonsAtom = atomWithStorage('nexa-show-action-buttons-v2', true);

// UX feature discovery atoms
export const reportTooltipCountAtom = atomWithStorage('nexa:ux:flag:report-tooltip-count', 0);

// Action Bubbles atoms
export const suggestedActionsAtom = atom<SuggestedAction[]>([]);
export const actionBubblesLoadingAtom = atom<boolean>(false);
